/**
 * Error classification for mutations.
 *
 * - "network": browser fetch failures, timeouts — retryable as-is
 * - "auth": session expired, not logged in — requires re-authentication
 * - "validation": RLS, unique constraint, FK violation — requires user edit
 * - "internal": IndexedDB failures, unexpected errors — may not be retryable
 */
export type ErrorClassification = "network" | "auth" | "validation" | "internal"

/**
 * Structured error for mutations that preserves Supabase PostgREST error
 * details (code, hint, details) through the error chain and through
 * JSON serialization for persistence/rehydration.
 */
export class MutationError extends Error {
  name = "MutationError" as const

  /** Supabase/PostgREST error code (e.g. "23505", "42501") */
  code: string | null

  /** Supabase hint text */
  hint: string | null

  /** Supabase details text */
  details: string | null

  /** Computed classification for UI guidance */
  classification: ErrorClassification

  constructor(opts: {
    message: string
    code?: string | null
    hint?: string | null
    details?: string | null
    classification?: ErrorClassification
  }) {
    super(opts.message)
    this.code = opts.code ?? null
    this.hint = opts.hint ?? null
    this.details = opts.details ?? null
    this.classification = opts.classification ?? classifyError(opts)
  }
}

/**
 * Type guard for MutationError.
 * Checks both instanceof and error.name to work after JSON deserialization.
 */
export function isMutationError(error: unknown): error is MutationError {
  if (error instanceof MutationError) return true
  return (
    error instanceof Error &&
    error.name === "MutationError" &&
    "classification" in error
  )
}

/**
 * Classify an error based on message and PostgREST code heuristics.
 */
function classifyError(opts: {
  message: string
  code?: string | null
}): ErrorClassification {
  const msg = opts.message.toLowerCase()

  // Network errors
  if (
    msg.includes("failed to fetch") ||
    msg.includes("network unavailable") ||
    msg.includes("networkerror") ||
    msg.includes("load failed") ||
    msg.includes("timeout") ||
    msg.includes("network request failed")
  ) {
    return "network"
  }

  // Auth errors
  if (
    msg.includes("no session") ||
    msg.includes("jwt expired") ||
    msg.includes("not authenticated") ||
    msg.includes("please login")
  ) {
    return "auth"
  }

  // PostgREST validation errors (by code prefix)
  const code = opts.code
  if (code) {
    // Class 23 = integrity constraint violations (FK, unique, check, etc.)
    if (code.startsWith("23")) return "validation"
    // Class 42 = access rule violations (RLS, insufficient privilege)
    if (code.startsWith("42")) return "validation"
  }

  // PostgREST message-based detection (when code is missing)
  if (
    msg.includes("violates") ||
    msg.includes("duplicate key") ||
    msg.includes("rls policy") ||
    msg.includes("not found in indexeddb")
  ) {
    return "validation"
  }

  return "internal"
}

/**
 * Wrap a Supabase PostgREST error into a MutationError.
 * Use in place of `throw new Error(error.message)`.
 */
export function fromSupabaseError(error: {
  message: string
  code?: string
  details?: string
  hint?: string
}): MutationError {
  let message = error.message ?? null

  if (error.message === "TypeError: Failed to fetch") {
    message = "Network Unavailable"
  }
  return new MutationError({
    message,
    code: error.code ?? null,
    hint: error.hint ?? null,
    details: error.details ?? null,
  })
}

/**
 * Classify any error (used by UI to determine retryability).
 * Works with both MutationError and plain Error.
 */
export function getErrorClassification(error: unknown): ErrorClassification {
  if (isMutationError(error)) return error.classification
  if (error instanceof Error) {
    return classifyError({ message: error.message })
  }
  return "internal"
}

/**
 * Whether an error classification means the user can retry without changes.
 */
export function isRetryable(classification: ErrorClassification): boolean {
  return classification === "network"
}
